import React, {
  useRef, useEffect, useState, useCallback, useMemo
} from "react"

const SHAPE_TOOLS = new Set(["line", "rectangle", "circle", "ellipse"])
const DRAW_TOOL_ID = "draw"
const SIGNATURE_IMAGE_TOOL_ID = "signatureImage"
const SIGNATURE_IMAGE_TYPE = "signature_image"
const SIGNATURE_BLOCK_TOOL_ID = "signatureBlock"
const SIGNATURE_BLOCK_TYPE = "signature_block"
const DEFAULT_SIGNATURE_WIDTH = 240
const DEFAULT_SIGNATURE_BLOCK_WIDTH = 200

// Helper to derive user title from role
const getUserTitle = role => {
  if (!role) return ""
  if (role.includes("registered_surveyors")) return "Registered Surveyor"
  if (role.includes("drafting")) return "Drafter"
  if (role.includes("operations")) return "Operations"
  if (role.includes("admin")) return "Administrator"
  return ""
}

/**
 * Simple Canvas-based annotation layer without external dependencies
 * Uses native HTML5 Canvas API for maximum compatibility
 */
export default function AnnotationCanvasSimple({
  width,
  height,
  annotations = [],
  onAnnotationsChange,
  currentTool = "select",
  currentPage = 1,
  canAnnotate = false,
  selectedStampType = "approved",
  toolSettings = {},
  selectedAnnotationId = null,
  onSelectionChange = null,
  userSignatureAsset = null,
  userInfo = null
}) {
  const canvasRef = useRef(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [startPos, setStartPos] = useState(null)
  const [selectedId, setSelectedId] = useState(selectedAnnotationId || null)
  const [forceRedraw, setForceRedraw] = useState(0)
  const [drawPath, setDrawPath] = useState([])
  const imageCacheRef = useRef(new Map())
  const selectedIdRef = useRef(selectedAnnotationId || null)
  
  // Drag state for moveable annotations
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState(null)
  const dragAnnotationOriginalRef = useRef(null)

  const resolvedTextOptions = useMemo(() => ({
    fontSize: toolSettings?.text?.fontSize || 16,
    color: toolSettings?.text?.color || "#d32f2f",
    fontWeight: toolSettings?.text?.fontWeight || "normal",
    fontFamily: toolSettings?.text?.fontFamily || "Arial"
  }), [toolSettings])

  const resolvedDrawOptions = useMemo(() => ({
    color: toolSettings?.draw?.color || "#0d47a1",
    lineWidth: toolSettings?.draw?.lineWidth || 2,
    lineStyle: toolSettings?.draw?.lineStyle || "smooth"
  }), [toolSettings])

  const resolvedHighlightOptions = useMemo(() => ({
    color: toolSettings?.highlight?.color || "#fff176",
    opacity: toolSettings?.highlight?.opacity || 0.5
  }), [toolSettings])

  const resolvedLineOptions = useMemo(() => ({
    strokeColor: toolSettings?.line?.strokeColor || "#c62828",
    strokeWidth: toolSettings?.line?.strokeWidth || 2
  }), [toolSettings])

  const resolvedRectangleOptions = useMemo(() => ({
    strokeColor: toolSettings?.rectangle?.strokeColor || "#c62828",
    strokeWidth: toolSettings?.rectangle?.strokeWidth || 2,
    fillEnabled: Boolean(toolSettings?.rectangle?.fillEnabled),
    fillColor: toolSettings?.rectangle?.fillColor || "#c62828",
    fillOpacity: typeof toolSettings?.rectangle?.fillOpacity === "number"
      ? toolSettings.rectangle.fillOpacity
      : 0.2
  }), [toolSettings])

  const resolvedCircleOptions = useMemo(() => ({
    strokeColor: toolSettings?.circle?.strokeColor || "#2e7d32",
    strokeWidth: toolSettings?.circle?.strokeWidth || 2,
    fillEnabled: Boolean(toolSettings?.circle?.fillEnabled),
    fillColor: toolSettings?.circle?.fillColor || "#2e7d32",
    fillOpacity: typeof toolSettings?.circle?.fillOpacity === "number"
      ? toolSettings.circle.fillOpacity
      : 0.2
  }), [toolSettings])

  const resolvedEllipseOptions = useMemo(() => ({
    strokeColor: toolSettings?.ellipse?.strokeColor || "#1565c0",
    strokeWidth: toolSettings?.ellipse?.strokeWidth || 2,
    fillEnabled: Boolean(toolSettings?.ellipse?.fillEnabled),
    fillColor: toolSettings?.ellipse?.fillColor || "#1565c0",
    fillOpacity: typeof toolSettings?.ellipse?.fillOpacity === "number"
      ? toolSettings.ellipse.fillOpacity
      : 0.2
  }), [toolSettings])

  const signatureAspectRatio = useMemo(() => {
    const naturalWidth = userSignatureAsset?.naturalWidth || 0
    const naturalHeight = userSignatureAsset?.naturalHeight || 0
    if (naturalWidth > 0 && naturalHeight > 0) {
      return naturalWidth / naturalHeight
    }
    return 3
  }, [userSignatureAsset])

  const defaultSignatureDimensions = useMemo(() => {
    const width = userSignatureAsset?.naturalWidth
      ? Math.min(DEFAULT_SIGNATURE_WIDTH, userSignatureAsset.naturalWidth)
      : DEFAULT_SIGNATURE_WIDTH
    const height = width / signatureAspectRatio
    return { width, height }
  }, [signatureAspectRatio, userSignatureAsset])

  const getImageFromCache = useCallback(url => {
    if (!url) return null
    const cached = imageCacheRef.current.get(url)
    if (cached && cached.status === "loaded") {
      return cached.image
    }
    if (!cached) {
      const img = new Image()
      img.crossOrigin = "anonymous"
      imageCacheRef.current.set(url, { status: "loading", image: img })
      img.onload = () => {
        imageCacheRef.current.set(url, { status: "loaded", image: img })
        setForceRedraw(prev => prev + 1)
      }
      img.onerror = () => {
        imageCacheRef.current.set(url, { status: "error", image: null })
      }
      img.src = url
    }
    return null
  }, [])

  useEffect(() => {
    if (userSignatureAsset?.url) {
      getImageFromCache(userSignatureAsset.url)
    }
  }, [userSignatureAsset, getImageFromCache])

  // Get annotations for current page
  const currentPageAnnotations = annotations.filter(a => a.page === currentPage)

  const shapeOptionMap = useMemo(() => ({
    line: resolvedLineOptions,
    rectangle: resolvedRectangleOptions,
    circle: resolvedCircleOptions,
    ellipse: resolvedEllipseOptions
  }), [resolvedLineOptions, resolvedRectangleOptions, resolvedCircleOptions, resolvedEllipseOptions])

  const buildShapeData = useCallback((tool, start, current, { allowSmall = false } = {}) => {
    if (!start || !current || !shapeOptionMap[tool]) {
      return null
    }

    const options = shapeOptionMap[tool]
    const deltaX = current.x - start.x
    const deltaY = current.y - start.y

    if (tool === "line") {
      const length = Math.hypot(deltaX, deltaY)
      if (!allowSmall && length < 5) {
        return null
      }
      return {
        start: { ...start },
        end: { ...current },
        strokeColor: options.strokeColor,
        strokeWidth: options.strokeWidth
      }
    }

    const rawWidth = deltaX
    const rawHeight = deltaY
    const absWidth = Math.abs(rawWidth)
    const absHeight = Math.abs(rawHeight)

    if (!allowSmall && (absWidth < 5 || absHeight < 5)) {
      return null
    }

    if (tool === "rectangle") {
      const x = rawWidth < 0 ? current.x : start.x
      const y = rawHeight < 0 ? current.y : start.y
      return {
        x,
        y,
        width: absWidth,
        height: absHeight,
        strokeColor: options.strokeColor,
        strokeWidth: options.strokeWidth,
        fillEnabled: Boolean(options.fillEnabled),
        fillColor: options.fillColor,
        fillOpacity: typeof options.fillOpacity === "number" ? options.fillOpacity : 0.2
      }
    }

    const baseX = rawWidth < 0 ? current.x : start.x
    const baseY = rawHeight < 0 ? current.y : start.y

    if (tool === "circle") {
      const size = Math.max(absWidth, absHeight)
      const adjustedX = rawWidth < 0 ? start.x - size : start.x
      const adjustedY = rawHeight < 0 ? start.y - size : start.y
      return {
        x: adjustedX,
        y: adjustedY,
        width: size,
        height: size,
        strokeColor: options.strokeColor,
        strokeWidth: options.strokeWidth,
        fillEnabled: Boolean(options.fillEnabled),
        fillColor: options.fillColor,
        fillOpacity: typeof options.fillOpacity === "number" ? options.fillOpacity : 0.2
      }
    }

    if (tool === "ellipse") {
      return {
        x: baseX,
        y: baseY,
        width: absWidth,
        height: absHeight,
        strokeColor: options.strokeColor,
        strokeWidth: options.strokeWidth,
        fillEnabled: Boolean(options.fillEnabled),
        fillColor: options.fillColor,
        fillOpacity: typeof options.fillOpacity === "number" ? options.fillOpacity : 0.2
      }
    }

    return null
  }, [shapeOptionMap])

  const buildSignatureImageData = useCallback(({ start, current, allowSmall = false }) => {
    if (!start || !current || !userSignatureAsset?.url) {
      return null
    }

    const deltaX = current.x - start.x
    const deltaY = current.y - start.y
    const absX = Math.abs(deltaX)
    const absY = Math.abs(deltaY)
    const minSize = 20

    let width = absX
    let height = width / signatureAspectRatio

    if (absY > height) {
      height = absY
      width = height * signatureAspectRatio
    }

    if (!allowSmall && (width < minSize || height < minSize)) {
      return null
    }

    const x = deltaX < 0 ? start.x - width : start.x
    const y = deltaY < 0 ? start.y - height : start.y

    return {
      x,
      y,
      width,
      height,
      imageUrl: userSignatureAsset.url,
      naturalWidth: userSignatureAsset.naturalWidth || defaultSignatureDimensions.width,
      naturalHeight: userSignatureAsset.naturalHeight || defaultSignatureDimensions.height,
      aspectRatio: signatureAspectRatio
    }
  }, [defaultSignatureDimensions.height, defaultSignatureDimensions.width, signatureAspectRatio, userSignatureAsset])

  const drawShapeAnnotation = useCallback((ctx, annotation) => {
    if (!annotation || !annotation.data) return
    const { type, data } = annotation
    ctx.save()
    const strokeColor = data.strokeColor || "#c62828"
    const strokeWidth = data.strokeWidth || 2
    ctx.strokeStyle = strokeColor
    ctx.lineWidth = strokeWidth
    ctx.lineJoin = "round"
    ctx.lineCap = "round"

    if (type === "line" && data.start && data.end) {
      ctx.beginPath()
      ctx.moveTo(data.start.x, data.start.y)
      ctx.lineTo(data.end.x, data.end.y)
      ctx.stroke()
    } else if (type === "rectangle" && typeof data.x === "number") {
      if (data.fillEnabled) {
        ctx.fillStyle = data.fillColor || strokeColor
        ctx.globalAlpha = typeof data.fillOpacity === "number" ? data.fillOpacity : 0.2
        ctx.fillRect(data.x, data.y, data.width, data.height)
        ctx.globalAlpha = 1
      }
      ctx.strokeRect(data.x, data.y, data.width, data.height)
    } else if ((type === "circle" || type === "ellipse") && typeof data.x === "number") {
      const radiusX = (data.width || 0) / 2
      const radiusY = (data.height || 0) / 2
      const centerX = data.x + radiusX
      const centerY = data.y + radiusY
      ctx.beginPath()
      ctx.ellipse(centerX, centerY, Math.max(radiusX, 0.5), Math.max(radiusY, 0.5), 0, 0, Math.PI * 2)
      if (data.fillEnabled) {
        ctx.fillStyle = data.fillColor || strokeColor
        ctx.globalAlpha = typeof data.fillOpacity === "number" ? data.fillOpacity : 0.2
        ctx.fill()
        ctx.globalAlpha = 1
      }
      ctx.stroke()
    }

    ctx.restore()
  }, [])

  const drawSignatureImageAnnotation = useCallback((ctx, annotation) => {
    if (!annotation?.data?.imageUrl) return
    const image = getImageFromCache(annotation.data.imageUrl)
    if (!image) {
      // Image still loading; trigger cache fetch
      getImageFromCache(annotation.data.imageUrl)
      return
    }

    const width = annotation.data.width || image.naturalWidth || defaultSignatureDimensions.width
    const height = annotation.data.height || image.naturalHeight || defaultSignatureDimensions.height
    ctx.drawImage(image, annotation.data.x, annotation.data.y, width, height)
  }, [defaultSignatureDimensions.height, defaultSignatureDimensions.width, getImageFromCache])

  const getAnnotationBoundingBox = useCallback(annotation => {
    if (!annotation || !annotation.data) return null
    const { type, data } = annotation

    if (type === "highlight" || type === "rectangle" || type === "circle" || type === "ellipse") {
      return {
        x: data.x,
        y: data.y,
        width: data.width || 0,
        height: data.height || 0
      }
    }

    if (type === "line" && data.start && data.end) {
      const minX = Math.min(data.start.x, data.end.x)
      const minY = Math.min(data.start.y, data.end.y)
      const width = Math.max(Math.abs(data.end.x - data.start.x), data.strokeWidth || 2)
      const height = Math.max(Math.abs(data.end.y - data.start.y), data.strokeWidth || 2)
      return {
        x: minX,
        y: minY,
        width,
        height
      }
    }

    if (type === "text") {
      const fontSize = data.fontSize || 16
      const estimatedWidth = Math.max((data.text?.length || 1) * fontSize * 0.6, 40)
      return {
        x: data.x,
        y: data.y - fontSize,
        width: estimatedWidth,
        height: fontSize + 8
      }
    }

    if (type === "signature" && Array.isArray(data.path) && data.path.length) {
      const xs = data.path.map(p => p.x)
      const ys = data.path.map(p => p.y)
      return {
        x: Math.min(...xs),
        y: Math.min(...ys),
        width: Math.max(...xs) - Math.min(...xs),
        height: Math.max(...ys) - Math.min(...ys)
      }
    }

    if (type === "stamp") {
      return {
        x: data.x,
        y: data.y,
        width: data.width || 120,
        height: data.height || 40
      }
    }

    if (type === SIGNATURE_IMAGE_TYPE) {
      return {
        x: data.x,
        y: data.y,
        width: data.width || defaultSignatureDimensions.width,
        height: data.height || defaultSignatureDimensions.height
      }
    }

    if (type === SIGNATURE_BLOCK_TYPE) {
      // Signature block height includes signature area + text lines
      const signatureHeight = 50
      const fontSize = data.fontSize || 12
      const lineHeight = fontSize * 1.4
      const textLines = [data.userName, data.userTitle, data.surveyorId].filter(Boolean).length
      const totalHeight = signatureHeight + 5 + (textLines * lineHeight)
      return {
        x: data.x,
        y: data.y,
        width: data.width || DEFAULT_SIGNATURE_BLOCK_WIDTH,
        height: totalHeight
      }
    }

    return null
  }, [defaultSignatureDimensions.height, defaultSignatureDimensions.width])

  // Generate unique ID
  const generateId = () => `ann_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

  const emitSelectionChange = useCallback(newId => {
    if (selectedIdRef.current === newId) {
      return
    }

    selectedIdRef.current = newId
    setSelectedId(newId)
    if (onSelectionChange) {
      onSelectionChange(newId)
    }
  }, [onSelectionChange])

  useEffect(() => {
    if (selectedAnnotationId !== undefined && selectedAnnotationId !== selectedIdRef.current) {
      selectedIdRef.current = selectedAnnotationId
      setSelectedId(selectedAnnotationId || null)
    }
  }, [selectedAnnotationId])

  // Draw all annotations on canvas
  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) {
      // eslint-disable-next-line no-console
      console.warn("Cannot redraw - canvas ref is null")
      return
    }

    const ctx = canvas.getContext("2d")
    if (!ctx) {
      // eslint-disable-next-line no-console
      console.warn("Cannot redraw - context is null")
      return
    }
    
    ctx.clearRect(0, 0, width, height)

    // eslint-disable-next-line no-console
    console.log(`Redrawing canvas - Page ${currentPage}, Annotations: ${currentPageAnnotations.length}`, {
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      propsWidth: width,
      propsHeight: height,
      annotations: currentPageAnnotations
    })

    // Draw each annotation
    currentPageAnnotations.forEach(annotation => {
      if (annotation.type === "highlight") {
        ctx.fillStyle = annotation.data.color || resolvedHighlightOptions.color
        ctx.globalAlpha = annotation.data.opacity || resolvedHighlightOptions.opacity
        ctx.fillRect(
          annotation.data.x,
          annotation.data.y,
          annotation.data.width,
          annotation.data.height
        )
        ctx.globalAlpha = 1.0
      } else if (annotation.type === "text") {
        const fontSize = annotation.data.fontSize || resolvedTextOptions.fontSize
        const fontFamily = annotation.data.fontFamily || resolvedTextOptions.fontFamily
        const fontWeight = annotation.data.fontWeight || resolvedTextOptions.fontWeight
        ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`
        ctx.fillStyle = annotation.data.color || resolvedTextOptions.color
        ctx.fillText(
          annotation.data.text,
          annotation.data.x,
          annotation.data.y
        )
      } else if (annotation.type === "signature") {
        // Draw signature path
        if (annotation.data.path && annotation.data.path.length > 0) {
          ctx.strokeStyle = annotation.data.color || resolvedDrawOptions.color
          ctx.lineWidth = annotation.data.lineWidth || resolvedDrawOptions.lineWidth
          const lineStyle = annotation.data.lineStyle || resolvedDrawOptions.lineStyle
          ctx.lineCap = lineStyle === "sharp" ? "butt" : "round"
          ctx.lineJoin = lineStyle === "sharp" ? "miter" : "round"
          
          ctx.beginPath()
          ctx.moveTo(annotation.data.path[0].x, annotation.data.path[0].y)
          
          annotation.data.path.forEach(point => {
            ctx.lineTo(point.x, point.y)
          })
          
          ctx.stroke()
        }
      } else if (annotation.type === SIGNATURE_IMAGE_TYPE) {
        drawSignatureImageAnnotation(ctx, annotation)
      } else if (annotation.type === "stamp") {
        const stampWidth = annotation.data.width || 120
        const stampHeight = annotation.data.height || 40
        
        // Draw border
        ctx.strokeStyle = annotation.data.color || "green"
        ctx.lineWidth = 3
        ctx.strokeRect(
          annotation.data.x,
          annotation.data.y,
          stampWidth,
          stampHeight
        )
        
        // Draw background
        ctx.fillStyle = "white"
        ctx.globalAlpha = 0.9
        ctx.fillRect(
          annotation.data.x,
          annotation.data.y,
          stampWidth,
          stampHeight
        )
        ctx.globalAlpha = 1.0
        
        // Draw text - use custom stamp text if provided, otherwise fall back to type-based text
        const { stampText: customText, stampType } = annotation.data
        let stampText = "STAMP"
        if (customText) {
          stampText = customText
        } else if (stampType === "approved") {
          stampText = "✓ APPROVED"
        } else if (stampType === "rejected") {
          stampText = "✗ REJECTED"
        } else if (stampType === "confidential") {
          stampText = "CONFIDENTIAL"
        } else if (stampType === "draft") {
          stampText = "DRAFT"
        } else if (stampType === "final") {
          stampText = "FINAL"
        }
        
        ctx.font = "bold 16px Arial"
        ctx.fillStyle = annotation.data.color || "green"
        ctx.textAlign = "center"
        ctx.textBaseline = "middle"
        ctx.fillText(
          stampText,
          annotation.data.x + stampWidth / 2,
          annotation.data.y + stampHeight / 2
        )
        ctx.textAlign = "left"
        ctx.textBaseline = "alphabetic"
      } else if (SHAPE_TOOLS.has(annotation.type)) {
        drawShapeAnnotation(ctx, annotation)
      } else if (annotation.type === SIGNATURE_BLOCK_TYPE) {
        // Draw signature block annotation
        const {
          x: blockX, y: blockY, width, fontSize: blockFontSize,
          signatureImageUrl, showSignatureLine, userName, userTitle, surveyorId
        } = annotation.data
        const blockWidth = width || DEFAULT_SIGNATURE_BLOCK_WIDTH
        const fontSize = blockFontSize || 12
        const lineHeight = fontSize * 1.4
        const signatureHeight = 50
        let currentY = blockY

        // Calculate total block height for border
        const textLines = [userName, userTitle, surveyorId].filter(Boolean).length
        const totalBlockHeight = signatureHeight + 5 + (textLines * lineHeight)

        // Draw subtle border to make block visible
        ctx.strokeStyle = "#ccc"
        ctx.lineWidth = 1
        ctx.setLineDash([3, 3])
        ctx.strokeRect(blockX - 2, blockY - 2, blockWidth + 4, totalBlockHeight + 4)
        ctx.setLineDash([])

        // Draw signature image if available
        if (signatureImageUrl) {
          const cachedImage = getImageFromCache(signatureImageUrl)
          if (cachedImage) {
            ctx.drawImage(cachedImage, blockX, currentY, blockWidth, signatureHeight)
          }
          currentY += signatureHeight + 5
        } else if (showSignatureLine) {
          // Draw signature line
          ctx.strokeStyle = "#000"
          ctx.lineWidth = 1
          ctx.beginPath()
          ctx.moveTo(blockX, currentY + signatureHeight - 5)
          ctx.lineTo(blockX + blockWidth, currentY + signatureHeight - 5)
          ctx.stroke()
          currentY += signatureHeight + 5
        }

        // Draw name
        if (userName) {
          ctx.font = `bold ${fontSize}px Arial`
          ctx.fillStyle = "#000"
          ctx.textAlign = "left"
          ctx.textBaseline = "top"
          ctx.fillText(userName, blockX, currentY)
          currentY += lineHeight
        }

        // Draw title
        if (userTitle) {
          ctx.font = `${fontSize}px Arial`
          ctx.fillStyle = "#000"
          ctx.fillText(userTitle, blockX, currentY)
          currentY += lineHeight
        }

        // Draw surveyor ID
        if (surveyorId) {
          ctx.font = `${fontSize}px Arial`
          ctx.fillStyle = "#000"
          ctx.fillText(`Surveyor ID: ${surveyorId}`, blockX, currentY)
        }
      }

      // Draw selection border if selected
      if (annotation.id === selectedId) {
        const bbox = getAnnotationBoundingBox(annotation)
        if (bbox) {
          ctx.strokeStyle = "blue"
          ctx.lineWidth = 2
          ctx.setLineDash([5, 5])
          ctx.strokeRect(
            bbox.x - 5,
            bbox.y - 5,
            (bbox.width || 0) + 10,
            (bbox.height || 0) + 10
          )
          ctx.setLineDash([])
        }
      }
    })
  }, [
    currentPageAnnotations,
    selectedId,
    width,
    height,
    resolvedHighlightOptions,
    resolvedTextOptions,
    resolvedDrawOptions,
    drawShapeAnnotation,
    drawSignatureImageAnnotation,
    getAnnotationBoundingBox
  ])

  // Redraw whenever anything changes - annotations, page, dimensions, or force trigger
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log(`AnnotationCanvas useEffect - redrawing (${annotations.length} total annotations, ${currentPageAnnotations.length} on page ${currentPage})`)
    
    // Small delay to ensure canvas is ready
    const timer = setTimeout(() => {
      redrawCanvas()
    }, 10)
    
    return () => clearTimeout(timer)
  }, [annotations, currentPage, width, height, redrawCanvas, currentPageAnnotations.length, forceRedraw])

  // Force initial redraw when component mounts
  useEffect(() => {
    if (annotations.length > 0) {
      // eslint-disable-next-line no-console
      console.log("Component mounted with existing annotations - forcing redraw")
      setForceRedraw(prev => prev + 1)
    }
  }, [])

  useEffect(() => {
    if (selectedIdRef.current && !annotations.some(ann => ann.id === selectedIdRef.current)) {
      emitSelectionChange(null)
    }
  }, [annotations, emitSelectionChange])

  useEffect(() => {
    if (currentTool !== "select" && selectedIdRef.current) {
      emitSelectionChange(null)
    }
  }, [currentTool, emitSelectionChange])

  const cursorStyle = useMemo(() => {
    if (currentTool === "select") {
      return "default"
    }
    if (currentTool === SIGNATURE_IMAGE_TOOL_ID) {
      return "copy"
    }
    return "crosshair"
  }, [currentTool])

  // Mouse event handlers
  const handleMouseDown = useCallback(e => {
    if (!canAnnotate) return

    const rect = canvasRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    setStartPos({ x, y })

    if (currentTool === DRAW_TOOL_ID) {
      setIsDrawing(true)
      setDrawPath([{ x, y }])
    } else if (currentTool === SIGNATURE_IMAGE_TOOL_ID) {
      if (!userSignatureAsset?.url) {
        // eslint-disable-next-line no-console
        console.warn("Cannot place signature image - missing signature asset")
        setIsDrawing(false)
        return
      }
      setIsDrawing(true)
    } else if (currentTool === "text") {
      // eslint-disable-next-line no-alert
      const text = prompt("Enter annotation text:")
      if (text && text.trim()) {
        const newAnnotation = {
          id: generateId(),
          type: "text",
          page: currentPage,
          data: {
            x,
            y,
            text: text.trim(),
            fontSize: resolvedTextOptions.fontSize,
            color: resolvedTextOptions.color,
            fontFamily: resolvedTextOptions.fontFamily,
            fontWeight: resolvedTextOptions.fontWeight
          }
        }
        onAnnotationsChange([...annotations, newAnnotation])
      }
    } else if (currentTool === "stamp") {
      // Use the pre-selected stamp type from the toolbar
      const stampTypes = {
        approved: { type: "approved", text: "✓ APPROVED", color: "green" },
        rejected: { type: "rejected", text: "✗ REJECTED", color: "red" },
        confidential: { type: "confidential", text: "CONFIDENTIAL", color: "red" },
        draft: { type: "draft", text: "DRAFT", color: "orange" },
        final: { type: "final", text: "FINAL", color: "blue" }
      }

      const selectedStamp = stampTypes[selectedStampType] || stampTypes.approved

      const newAnnotation = {
        id: generateId(),
        type: "stamp",
        page: currentPage,
        data: {
          x,
          y,
          stampType: selectedStamp.type,
          stampText: selectedStamp.text,
          width: 120,
          height: 40,
          color: selectedStamp.color
        }
      }
      onAnnotationsChange([...annotations, newAnnotation])
    } else if (currentTool === SIGNATURE_BLOCK_TOOL_ID) {
      // Create signature block with user info
      const userName = userInfo
        ? `${userInfo.firstName || ""} ${userInfo.surname || ""}`.trim()
        : ""
      const userTitle = getUserTitle(userInfo?.role)
      const surveyorId = userInfo?.surveyorId || ""
      const signatureUrl = userInfo?.signaturePath || null

      const newAnnotation = {
        id: generateId(),
        type: SIGNATURE_BLOCK_TYPE,
        page: currentPage,
        data: {
          x,
          y,
          width: DEFAULT_SIGNATURE_BLOCK_WIDTH,
          height: 100,
          signatureImageUrl: signatureUrl,
          userName,
          userTitle,
          surveyorId,
          showSignatureLine: !signatureUrl,
          fontSize: toolSettings?.signatureBlock?.fontSize || 12
        }
      }
      onAnnotationsChange([...annotations, newAnnotation])
      // Force redraw on next animation frame after state update propagates
      requestAnimationFrame(() => {
        setTimeout(() => setForceRedraw(prev => prev + 1), 0)
      })
    } else if (currentTool === "highlight") {
      setIsDrawing(true)
    } else if (SHAPE_TOOLS.has(currentTool)) {
      setIsDrawing(true)
    } else if (currentTool === "select") {
      // Find clicked annotation
      const clicked = currentPageAnnotations.find(ann => {
        const bbox = getAnnotationBoundingBox(ann)
        if (!bbox) return false
        const padding = 6
        const withinX = x >= (bbox.x - padding) && x <= (bbox.x + bbox.width + padding)
        const withinY = y >= (bbox.y - padding) && y <= (bbox.y + bbox.height + padding)
        return withinX && withinY
      })
      
      if (clicked) {
        emitSelectionChange(clicked.id)
        // Start dragging
        setIsDragging(true)
        setDragStart({ x, y })
        // Store a deep copy of the original annotation data for drag offset calculation
        dragAnnotationOriginalRef.current = JSON.parse(JSON.stringify(clicked))
      } else {
        emitSelectionChange(null)
        setIsDragging(false)
        setDragStart(null)
        dragAnnotationOriginalRef.current = null
      }
    }
  }, [
    canAnnotate,
    currentTool,
    currentPage,
    annotations,
    onAnnotationsChange,
    currentPageAnnotations,
    resolvedTextOptions,
    userSignatureAsset,
    userInfo,
    toolSettings,
    emitSelectionChange,
    getAnnotationBoundingBox
  ])

  const handleMouseMove = useCallback(e => {
    const rect = canvasRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    // Handle dragging in select mode
    if (isDragging && dragStart && dragAnnotationOriginalRef.current && currentTool === "select") {
      const dx = x - dragStart.x
      const dy = y - dragStart.y
      const original = dragAnnotationOriginalRef.current
      
      // Find and update the annotation being dragged
      const updatedAnnotations = annotations.map(ann => {
        if (ann.id !== original.id) return ann
        
        // Create updated annotation with new position
        const updated = { ...ann, data: { ...ann.data } }
        
        // Update position based on annotation type
        if (ann.type === "text" || ann.type === "stamp" || ann.type === "highlight"
            || ann.type === "rectangle" || ann.type === "circle" || ann.type === "ellipse"
            || ann.type === SIGNATURE_IMAGE_TYPE || ann.type === SIGNATURE_BLOCK_TYPE) {
          updated.data.x = original.data.x + dx
          updated.data.y = original.data.y + dy
        } else if (ann.type === "line") {
          // Lines have start/end points
          if (original.data.start && original.data.end) {
            updated.data.start = { x: original.data.start.x + dx, y: original.data.start.y + dy }
            updated.data.end = { x: original.data.end.x + dx, y: original.data.end.y + dy }
          }
        } else if (ann.type === "draw" || ann.type === DRAW_TOOL_ID) {
          // Freehand drawings have a path array
          if (original.data.path && Array.isArray(original.data.path)) {
            updated.data.path = original.data.path.map(point => ({
              x: point.x + dx,
              y: point.y + dy
            }))
          }
        }
        
        return updated
      })
      
      onAnnotationsChange(updatedAnnotations)
      return
    }

    if (!isDrawing || !startPos) return

    if (currentTool === DRAW_TOOL_ID) {
      const newPath = [...drawPath, { x, y }]
      setDrawPath(newPath)

      redrawCanvas()
      const ctx = canvasRef.current.getContext("2d")
      ctx.strokeStyle = resolvedDrawOptions.color
      ctx.lineWidth = resolvedDrawOptions.lineWidth
      ctx.lineCap = resolvedDrawOptions.lineStyle === "sharp" ? "butt" : "round"
      ctx.lineJoin = resolvedDrawOptions.lineStyle === "sharp" ? "miter" : "round"

  const originPoint = drawPath[0] || startPos
  ctx.beginPath()
  ctx.moveTo(originPoint.x, originPoint.y)
      newPath.forEach(point => {
        ctx.lineTo(point.x, point.y)
      })
      ctx.stroke()
    } else if (currentTool === SIGNATURE_IMAGE_TOOL_ID) {
      redrawCanvas()
      const previewData = buildSignatureImageData({
        start: startPos,
        current: { x, y },
        allowSmall: true
      })
      if (previewData) {
        const ctx = canvasRef.current.getContext("2d")
        const previewImage = getImageFromCache(previewData.imageUrl)
        if (previewImage) {
          ctx.globalAlpha = 0.85
          ctx.drawImage(previewImage, previewData.x, previewData.y, previewData.width, previewData.height)
          ctx.globalAlpha = 1
        } else {
          ctx.setLineDash([6, 4])
          ctx.strokeStyle = "#0d47a1"
          ctx.strokeRect(previewData.x, previewData.y, previewData.width, previewData.height)
          ctx.setLineDash([])
        }
      }
    } else if (currentTool === "highlight") {
      // Redraw canvas with preview
      redrawCanvas()

      const ctx = canvasRef.current.getContext("2d")
      ctx.fillStyle = resolvedHighlightOptions.color
      ctx.globalAlpha = resolvedHighlightOptions.opacity
      ctx.fillRect(
        startPos.x,
        startPos.y,
        x - startPos.x,
        y - startPos.y
      )
      ctx.globalAlpha = 1.0
    } else if (SHAPE_TOOLS.has(currentTool)) {
      redrawCanvas()
      const previewData = buildShapeData(currentTool, startPos, { x, y }, { allowSmall: true })
      if (previewData) {
        const ctx = canvasRef.current.getContext("2d")
        drawShapeAnnotation(ctx, {
          id: "preview",
          type: currentTool,
          data: previewData
        })
      }
    }
  }, [
    isDrawing,
    isDragging,
    dragStart,
    startPos,
    currentTool,
    annotations,
    onAnnotationsChange,
    redrawCanvas,
    drawPath,
    resolvedDrawOptions,
    resolvedHighlightOptions,
    buildShapeData,
    drawShapeAnnotation,
    buildSignatureImageData,
    getImageFromCache
  ])

  const handleMouseUp = useCallback(e => {
    // End dragging if we were dragging
    if (isDragging) {
      setIsDragging(false)
      setDragStart(null)
      dragAnnotationOriginalRef.current = null
      // The annotation position has already been updated in handleMouseMove
      // so we just need to clean up the drag state
      return
    }

    if (!isDrawing || !startPos) {
      setIsDrawing(false)
      return
    }

    if (currentTool === DRAW_TOOL_ID) {
      let pathToPersist = drawPath
      if (!pathToPersist.length && startPos) {
        pathToPersist = [startPos]
      }

      if (pathToPersist.length === 1) {
        const point = pathToPersist[0]
        pathToPersist = [{ ...point }, { x: point.x + 0.4, y: point.y + 0.4 }]
      }

      if (pathToPersist.length >= 2) {
        const newAnnotation = {
          id: generateId(),
          type: "signature",
          page: currentPage,
          data: {
            path: pathToPersist,
            color: resolvedDrawOptions.color,
            lineWidth: resolvedDrawOptions.lineWidth,
            lineStyle: resolvedDrawOptions.lineStyle
          }
        }
        onAnnotationsChange([...annotations, newAnnotation])
      }
      setDrawPath([])
    } else if (currentTool === SIGNATURE_IMAGE_TOOL_ID) {
      if (userSignatureAsset?.url) {
        const rect = canvasRef.current.getBoundingClientRect()
        const pointer = {
          x: e.clientX - rect.left,
          y: e.clientY - rect.top
        }
        let signatureData = buildSignatureImageData({ start: startPos, current: pointer })

        if (!signatureData) {
          signatureData = {
            x: startPos.x,
            y: startPos.y,
            width: defaultSignatureDimensions.width,
            height: defaultSignatureDimensions.height,
            imageUrl: userSignatureAsset.url,
            naturalWidth: userSignatureAsset.naturalWidth || defaultSignatureDimensions.width,
            naturalHeight: userSignatureAsset.naturalHeight || defaultSignatureDimensions.height,
            aspectRatio: signatureAspectRatio
          }
        }

        if (signatureData) {
          const newAnnotation = {
            id: generateId(),
            type: SIGNATURE_IMAGE_TYPE,
            page: currentPage,
            data: signatureData
          }
          onAnnotationsChange([...annotations, newAnnotation])
        }
      }
    } else if (currentTool === "highlight") {
      const rect = canvasRef.current.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top

      const width = x - startPos.x
      const height = y - startPos.y

      // Only create if significant size
      if (Math.abs(width) > 5 && Math.abs(height) > 5) {
        const newAnnotation = {
          id: generateId(),
          type: "highlight",
          page: currentPage,
          data: {
            x: width < 0 ? x : startPos.x,
            y: height < 0 ? y : startPos.y,
            width: Math.abs(width),
            height: Math.abs(height),
            color: resolvedHighlightOptions.color,
            opacity: resolvedHighlightOptions.opacity
          }
        }
        onAnnotationsChange([...annotations, newAnnotation])
      }
    } else if (SHAPE_TOOLS.has(currentTool)) {
      const rect = canvasRef.current.getBoundingClientRect()
      const pointer = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      }
      const shapeData = buildShapeData(currentTool, startPos, pointer)
      if (shapeData) {
        const newAnnotation = {
          id: generateId(),
          type: currentTool,
          page: currentPage,
          data: shapeData
        }
        onAnnotationsChange([...annotations, newAnnotation])
      }
    }

    setIsDrawing(false)
    setStartPos(null)
  }, [
    isDrawing,
    isDragging,
    startPos,
    currentTool,
    currentPage,
    annotations,
    onAnnotationsChange,
    drawPath,
    resolvedDrawOptions,
    resolvedHighlightOptions,
    buildShapeData,
    buildSignatureImageData,
    userSignatureAsset,
    defaultSignatureDimensions,
    signatureAspectRatio
  ])

  if (!canAnnotate) {
    return null
  }

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        zIndex: 10,
        cursor: cursorStyle,
        pointerEvents: "auto"
      }} />
  )
}
